import type { Uuid } from "spacetimedb";
import { useState } from "react";
import { useRutterLink } from "react-rutter-link";

import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Spinner } from "@/web/components/primitives/spinner";
import { toaster } from "@/web/components/primitives/toast";
import { env } from "@/web/lib/env";
import { ErrorHandler } from "@/web/lib/errors";
import { useProcedureMutation } from "@/web/lib/tanstack";
import { procedures } from "@tycho/web-bindings";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";
import { usePostHog } from "@posthog/react";
import { PosthogEvents } from "@/web/lib/posthog-events";

type ConnectPosProps = {
  /** Underwritable entity that will own the resulting pos item. */
  underwritableEntity: Uuid;
};

function ConnectPosAction({
  underwritableEntity,
  onSkip,
  onComplete,
}: ConnectPosProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ActionCard category="budget">
        <ActionCard.Header>
          <ActionCard.Badge>Budget</ActionCard.Badge>
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>Connect point of sale</ActionCard.Title>
          <ActionCard.Description>
            Link Square, Clover, Shopify, or another POS so we can verify transaction volume
            automatically.
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>Connect</ActionCard.PrimaryButton>
        </ActionCard.Footer>
      </ActionCard>

      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Connect point of sale</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          {/*
            Rutter opens its own modal outside of our drawer once `open()` is
            called, so we only need to surface a launcher + pending state here.
          */}
          <RutterLaunchBody
            underwritableEntity={underwritableEntity}
            onComplete={() => {
              setOpen(false);
              onComplete();
            }}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}

function RutterLaunchBody({
  underwritableEntity,
  onComplete,
}: {
  underwritableEntity: Uuid;
  onComplete: () => void;
}) {
  const posthog = usePostHog();
  const connectPosMutation = useProcedureMutation(procedures.connectPos, {
    onSuccess: () => {
      PosthogEvents.PosConnected(posthog, underwritableEntity);
      toaster({ message: "POS connected", variant: "success" });
      onComplete();
    },
    onError: ErrorHandler.default,
  });

  const { open: openRutter, ready } = useRutterLink({
    publicKey: env.VITE_RUTTER_PUBLIC_KEY,
    onSuccess: (publicToken: string) => {
      connectPosMutation.mutate({
        underwritableEntity,
        rutterPublicToken: publicToken,
      });
    },
  });

  const isPending = connectPosMutation.isPending;

  return (
    <>
      <DrawerBody className="flex flex-col items-center justify-center gap-3">
        {isPending ? (
          <>
            <Spinner size="medium" />
            <p className="text-sm text-muted-foreground">Finalizing your connection…</p>
          </>
        ) : (
          <p className="text-center text-sm text-muted-foreground">
            You'll be prompted to pick your point-of-sale provider in a secure Rutter window.
          </p>
        )}
      </DrawerBody>
      <DrawerFooter>
        <Button color="blue" size="lg" disabled={!ready || isPending} onClick={() => openRutter()}>
          {isPending ? "Connecting…" : "Launch POS connection"}
        </Button>
      </DrawerFooter>
    </>
  );
}

export function createConnectPosAction(
  props: ConnectPosProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "connect-pos", canSkip = true, ...rest } = props;
  return {
    id,
    category: "budget",
    canSkip,
    render: (core) => <ConnectPosAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import type { PosConnection } from "@tycho/web-bindings/types";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createConnectPosCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/ConnectPosCompleted";

export const connectPosRule = CardRulesEngine.singletonRule({
  id: "connect-pos",
  priority: 50,
  when: CardRulesEngine.and(
    CardRulesEngine.missingConnection("pos"),
    CardRulesEngine.not(CardRulesEngine.hasNoScheduledCalls),
  ),
  build: (d) => createConnectPosAction({ underwritableEntity: d.underwritableEntityId }),
});

export const connectPosCompletedRule = CardRulesEngine.forEachCompletedRule<PosConnection>({
  id: "connect-pos:done",
  forEach: CardRulesEngine.eachConnection("pos"),
  when: CardRulesEngine.always,
  build: (_, connection) => createConnectPosCompleted({ connection }),
});
